import React, { useEffect, useRef, useState } from "react";
import { subscribe } from "@/lib/loading";

/**
 * Thin, top-of-viewport progress bar that animates whenever ANY API
 * request is in flight. Drops the burden of per-page loading
 * indicators since every fetch through the axios client trips this.
 *
 * Behaviour:
 *   - We wait 250ms BEFORE showing the bar. Requests that finish
 *     faster than that (~most cached GETs) never trigger it — kills
 *     the brief top-left flash users were seeing on snappy pages.
 *   - First slow request: bar slides in from 0% → 30% over 200ms.
 *   - While requests continue: bar creeps toward 90% asymptotically
 *     (never quite reaches 100% until the queue drains).
 *   - When the queue is empty: bar snaps to 100% and fades out.
 *
 * Implementation notes:
 *   - z-index 9999 so it sits over modals.
 *   - Solid crimson `#C1272D`. The earlier gold-tipped gradient read
 *     as a "weird gold strip" on fast page loads because at low
 *     width the gold edge sits right where the bar begins. Solid
 *     color is calmer and ties to the rest of the brand restraint.
 *   - The creep timer runs at 200ms so it doesn't burn battery on
 *     idle pages.
 */
const SHOW_DELAY_MS = 250;

export default function TopProgressBar() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const inFlightRef = useRef(0);
  const creepRef = useRef(null);
  const hideRef = useRef(null);
  const showDelayRef = useRef(null);

  useEffect(() => {
    return subscribe((n) => {
      inFlightRef.current = n;
      if (n > 0) {
        // Cancel any pending fade-out from a prior burst.
        if (hideRef.current) {
          clearTimeout(hideRef.current);
          hideRef.current = null;
        }
        // Only show the bar if a request is still in flight 250ms
        // from now — quick requests (cached GETs, etc.) finish before
        // this fires and the user sees nothing.
        if (!showDelayRef.current && !visible) {
          showDelayRef.current = setTimeout(() => {
            showDelayRef.current = null;
            if (inFlightRef.current === 0) return;
            setVisible(true);
            setProgress((p) => (p < 30 ? 30 : p));
            // Start creeping toward 90% while requests remain.
            if (!creepRef.current) {
              creepRef.current = setInterval(() => {
                setProgress((p) => {
                  if (inFlightRef.current === 0) return p;
                  // Asymptotic creep — each tick adds 10% of the
                  // remaining gap to 90, so we approach but never
                  // touch it.
                  const next = p + (90 - p) * 0.1;
                  return Math.min(next, 89.5);
                });
              }, 200);
            }
          }, SHOW_DELAY_MS);
        }
      } else {
        // Drain. Cancel a pending show-delay so nothing appears at all
        // for a request that finished inside the 250ms window.
        if (showDelayRef.current) {
          clearTimeout(showDelayRef.current);
          showDelayRef.current = null;
        }
        if (creepRef.current) {
          clearInterval(creepRef.current);
          creepRef.current = null;
        }
        if (!visible) {
          // Nothing was ever shown — no need to animate a fade.
          setProgress(0);
          return;
        }
        setProgress(100);
        hideRef.current = setTimeout(() => {
          setVisible(false);
          setProgress(0);
          hideRef.current = null;
        }, 350);
      }
    });
  }, [visible]);

  // Unmount safety.
  useEffect(() => () => {
    if (creepRef.current) clearInterval(creepRef.current);
    if (hideRef.current) clearTimeout(hideRef.current);
    if (showDelayRef.current) clearTimeout(showDelayRef.current);
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="top-progress-bar"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: 3,
        zIndex: 9999,
        pointerEvents: "none",
        opacity: visible ? 1 : 0,
        transition: "opacity 280ms ease-out",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${progress}%`,
          background: "#C1272D",
          boxShadow: "0 0 8px rgba(193,39,45,0.45)",
          transition: "width 240ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
    </div>
  );
}
